<template>
  <div
    :class='[
      "task-wrapper relative select-none rounded-[3px] [-webkit-touch-callout:none]",
      "[&.sortable-drag]:rotate-[1deg] [&.sortable-drag]:brightness-[1.01]",
      "[&.sortable-ghost]:rotate-[1deg] [&.sortable-ghost]:brightness-[1.01]",
      { new: !task, "from-stage ml-2": fromStage },
    ]' ref='taskRef' :id='task ? task.id : 0'
    @contextmenu.prevent
  >
    <div
      v-if="simplified"
      class="task"
    >
      <div
        class="relative flex flex-col gap-2 overflow-hidden rounded-card
               border border-line bg-card p-3"
      >
        <div v-if="skeleton && skeleton.projectStage" class="flex items-center justify-between">
          <SkeletonLoader type="text" class="h-3! w-14!" />
          <SkeletonLoader type="text" class="h-3! w-11!" />
        </div>
        <SkeletonLoader
          v-if="skeleton && skeleton.image"
          type="image"
          class="h-36! w-full! rounded-chip!"
        />
        <div class="flex flex-col gap-1.5">
          <SkeletonLoader
            v-for="(width, i) in skeletonRows"
            :key="`row-${i}`"
            type="text"
            :size="width"
            class="h-3.5!"
          />
        </div>
        <div v-if="skeleton && skeleton.tags" class="flex items-center gap-1.5">
          <SkeletonLoader type="text" class="h-4.5! w-13! rounded-lg!" />
          <SkeletonLoader type="text" class="h-4.5! w-9! rounded-lg!" />
        </div>
        <div class="flex h-8 items-center gap-2">
          <SkeletonLoader type="text" class="h-4.5! w-15! rounded-chip!" />
          <SkeletonLoader type="avatar" class="ml-auto size-6!" />
        </div>
      </div>
    </div>
    <!-- off-screen cards of long columns skip style, layout and paint until scrolled to; 8rem until first measured -->
    <div
      class="task hoverable-task group cursor-pointer [content-visibility:auto] [contain-intrinsic-size:auto_8rem]"
      @pointerenter.once="labelsShown = true"
      :class="{
        'animate-flash-update': shouldFlash && !fromStage,
        'animate-flash-update-from-stage': shouldFlash && fromStage,
        'from-stage': fromStage,
        'has-preview': !!(mainAttachment && mainAttachment.width),
      }"
      v-else
    >
      <!-- <div
        :title="JSON.stringify(task)"
      >ℹ️</div> -->
      <div
        class="project-bg relative flex flex-col gap-2 overflow-hidden rounded-card
               border border-line bg-card p-3
               [&>*]:relative [&>*]:z-[1]
               before:pointer-events-none before:absolute before:inset-0 before:content-['']
               before:bg-[image:var(--proj-bg)] before:bg-cover before:bg-center
               before:opacity-10 before:grayscale light:before:opacity-5 light:before:brightness-150
               after:pointer-events-none after:absolute after:inset-0 after:content-['']
               after:rounded-[inherit] after:bg-[color-mix(in_srgb,var(--ink)_5%,transparent)]
               after:opacity-0 after:transition-opacity after:duration-150
               group-hover:after:opacity-100"
        :style="{ '--proj-bg': !fromStage && getProjBg ? `url(${getProjBg})` : 'none' }"
        @click="emit('click')"
      >
      <div v-if="showStageAndProject" class="stage-info relative">
        <div
          v-if="showStageAndProject && task.stage"
          class="stage-name absolute right-0 top-0 text-label font-medium text-text-muted"
        >
          {{ task.stage.name }}
        </div>
        <div
          v-if="showStageAndProject && task.project"
          class="project-name max-w-31 truncate text-label font-medium text-text-muted"
        >
          {{ task.project.name }}
        </div>
      </div>
      <div
        class="preview-box h-36 overflow-hidden rounded-chip border border-line"
        v-if="!hidePreview && mainAttachment && mainAttachment.width"
        @click.stop
      >
      <Picture
        zoomable
        cover
        preset="preview"
        class="taskPreview"
        :style="{
          backgroundImage: previewBackdrop ? `url(${previewBackdrop})` : null,
        }"
        :uploaded="mainAttachment.uploaded"
        :basePicture="mainAttachment.basePicture"
        :url="mainAttachmentUrl || ''"
        :thumbHash="mainAttachment.thumbHash || ''"
        :color="mainAttachment.dominantColor || ''"
        :imageWidth="mainAttachment.width.toString()"
        :imageHeight="mainAttachment.height.toString()"
        :attachment="mainAttachment"
      />
      </div>
      <TaskUpdBadge
        class="absolute! top-0 right-0 size-[1.375rem] text-accent opacity-90"
        v-if="shouldShowUpd"
      />
      <div
        class="task-container contents"
        :id='task ? `clickable_${task.id}` : ``'
      >
        <div
          v-if="taskContentHidden"
          class="pointer-events-none absolute inset-0 z-2 flex items-center justify-center"
        >
          <HoverableTooltip :text="hiddenTaskTooltip">
            <span
              class="pointer-events-auto relative flex size-8.5 items-center justify-center rounded-full
                     border border-line bg-backdrop text-text shadow-[0_2px_8px_rgba(0,0,0,0.26)] outline-none"
              tabindex="0"
              :aria-label="hiddenTaskTooltip"
            >
              <IconLockClosed16Solid class="size-[1.0625rem]" />
            </span>
          </HoverableTooltip>
        </div>
        <div class="flex w-full items-start gap-2">
          <div
            v-if="task.textMD || computedParentPrefix"
            class="task-text w-full min-h-fit line-clamp-3 break-words
                   text-body font-regular leading-[1.4] tracking-[-0.005em] text-text">
            <template v-if="fromStage && isSubtask && computedParentPrefix">
              <span class="mr-1.5 whitespace-nowrap font-medium text-text-muted">
                {{ computedParentPrefix }}
              </span>
              <IconArrowRightCircle16Solid class="mr-1.5 align-text-bottom text-text-muted" />
              <span class="text-text">
                {{ taskShortDescription }}
              </span>
            </template>
            <template v-else>
              {{ taskShortDescription }}
            </template>
          </div>
          <div
            v-if="estimateCollector"
            class="estimate shrink-0 flex items-center gap-1 text-accent"
            :class="fromStage ? 'h-[1.0625rem]' : 'h-4'"
          >
            <span class="text-label font-bold">{{ estimateCollector }}</span>
            <span class="text-label font-medium">{{ $t('task.est') }}</span>
          </div>
          <IconHeart16Solid v-if="shouldShowLiked" class="shrink-0 -mr-1 size-4.5 text-accent" />
          <div
            v-if="!!task.priority || notify"
            class="priority flex size-4 shrink-0 flex-col items-center text-accent"
            :title="utils.verbalPriority(task)"
          >
            <i
              v-if='!!task.priority'
              class="flex size-[16px] items-center justify-center text-[16px] leading-none"
              :class="`tr tr-priority-${task.priority}`">
            </i>
            <i
              v-if="notify"
              class="tr tr-message-bubble relative mt-auto size-2.5 text-text-muted
                     before:absolute before:z-1 before:text-[0.625rem] before:text-accent
                     after:z-3 after:mx-auto after:mt-0.5 after:block after:size-[3px] after:scale-200
                     after:animate-growing after:rounded-full after:bg-accent after:opacity-50 after:content-['']">
            </i>
          </div>
        </div>
        <div
          v-if="task"
          class="task-data contents">
          <div
            v-if="checklist.length
              || (task.labels && task.labels.length) || (task.taskLabels && task.taskLabels.length)"
            class="flex flex-col gap-2">
            <div
              v-if="task.labels && task.labels.length"
              class="-mr-3.5 flex max-h-[1.5625rem] w-full gap-1.5 overflow-hidden">
              <div
                v-for="tag in task.labels"
                :key="tag.id"
                class="tag tag-tint shrink min-w-5 h-4.5 px-2 py-0.5 leading-none
                       flex items-center justify-center
                       rounded-lg font-bold text-unit overflow-hidden"
                :style="{ '--tag-color': `#${tag.color}` }"
              >
                <div class="truncate" :title="tag.name">
                  {{ tag.name }}
                </div>
              </div>
            </div>
            <ChecklistProgress
              v-if="checklist.length"
              :checklist="checklist"/>
          </div>
          <div class="tracker-data-priority-and-messages relative mt-auto flex h-8 max-h-8 w-full items-center">
            <TrackingCounters
              v-if="spentTimes.length"
              :task="task"
              :spentTimes="spentTimes"
              @pointerdown.stop
            />
            <!-- @pointerdown.stop prevents Sortable.js from triggering on click -->
            <div class="task-meta-right ml-auto flex items-center gap-2">
              <div
                class="labels-icon"
                @pointerdown.stop
              >
                <slot v-if="labelsShown" name='label'></slot>
              </div>
              <TrackingAvatars
                v-if="spentTimes.length"
                :task="task"
                :spentTimes="spentTimes"
                @pointerdown.stop
              />
              <div class="flex items-center"
                @pointerdown.stop
              >
              <!-- @pointerdown.stop prevents Sortable.js from triggering on click -->
                <Dropdown
                v-if='assigneeSelectList'
                class="z-1 justify-center"
                closeOnClick
                stickRight
                @click.stop
              >
                <template v-slot:wrapper>
                  <!-- if assigned and is executor -->
                  <UserAvatar :noUserText="t('ui.task_click_to_assign')"
                    :user='task.assigneeId ? getUser(task.assigneeId) : null' class='avatar size-6!' withDayoffBadge/>
                </template>
                <template v-slot:items>
                  <AppSearchBox
                    class="w-80"
                    :items="assigneeSelectList(task, assigneeList, getUser)"
                    :getSearchText="user => userSearchText(user, t('task.Unassign'))"
                    :placeholder="t('task.SEARCH_ASSIGNEE')"
                    @select="updateAssignee"
                  >
                    <template #item="{ item }">
                      <UserAvatar withDayoffBadge :user="item?.id ? item : null" />
                      <div class="truncate text-left">{{ item?.id ? item.username : t('task.Unassign') }}</div>
                    </template>
                  </AppSearchBox>
                </template>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ChecklistProgress from '@/components/elements/ChecklistProgress';
import Picture from '@/components/elements/Picture';
import SkeletonLoader from '@/components/elements/SkeletonLoader';
import TrackingCounters from '@/components/elements/TrackingCounters';
import TrackingAvatars from '@/components/elements/TrackingAvatars.vue';
import TaskUpdBadge from '@/components/elements/TaskUpdBadge.vue';
import HoverableTooltip from '@/components/elements/HoverableTooltip.vue';
import AppSearchBox from '@/components/elements/AppSearchBox.vue';
import { userSearchText } from '@/utils';
import { thumbHashToImageDataUrl } from '@/store/utils';
import { useTaskSpentTimes } from '@/composables/useTaskSpentTimes';
import { IconArrowRightCircle16Solid, IconLockClosed16Solid, IconHeart16Solid } from '@iconify-prerendered/vue-heroicons';
import { IconClock } from '@iconify-prerendered/vue-tabler';

const SKELETON_ROWS = {
  1: ['threequarters'],
  2: ['fullwidth', 'halfwidth'],
  3: ['fullwidth', 'fullwidth', 'halfwidth'],
};

// Props
const props = defineProps({
  task: Object,
  stage: Object,
  assigneeSelectList: Function,
  assigneeList: Array,
  skeleton: {
    type: Object,
    default: null,
  },
  hidePreview: {
    type: Boolean,
    default: false,
  },
  fromStage: {
    type: Boolean,
    default: false,
  },
  simplified: {
    type: Boolean,
    default: true,
  },
  showStageAndProject: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['click', 'update']);

// Store and router
const store = useStore();
const { t } = useI18n();

// Refs
const taskRef = ref(null);

// Data
// the labels picker is shown only on hover (see .labels-icon), so it is mounted on the first one
const labelsShown = ref(false);
const shouldFlash = ref(false);
const intersectionObserver = ref(null);
const hasBeenInViewport = ref(false);

// Store getters and state
const user = computed(() => store.state.user.user);
const getUser = computed(() => store.getters['user/getUser']);
const projectsList = computed(() => store.state.project.projectsList);

// Computed properties
const skeletonRows = computed(() => props.skeleton?.rows || SKELETON_ROWS[2]);

const checklist = computed(() => (props.task.checklist ? JSON.parse(props.task.checklist) : []));

// computed once for both the time and the avatars of the card
const spentTimes = useTaskSpentTimes(() => props.task);

const ttbuOfCurrentUser = computed(() => {
  if (!user.value) return null;
  return props.task?.taskTrackedByUsers?.find(x => x.userId === user.value.id);
});

const shouldShowUpd = computed(() => {
  const ttbu = ttbuOfCurrentUser.value;
  return ttbu && Date.parse(props.task.lastEventTs) > Date.parse(ttbu.tsOfLastEventIveSeen);
});

const shouldShowLiked = computed(() => {
  const ttbu = ttbuOfCurrentUser.value;
  return ttbu && ttbu.liked;
});

const notify = computed(() => {
  if (!props.task || !props.task.taskTrackedByUsers || !user.value) {
    return '';
  }
  const ttbuinfo = props.task.taskTrackedByUsers.find(el => el.userId === user.value.id);
  return (ttbuinfo && ttbuinfo.commentsToRead
    && ttbuinfo.commentsToRead > 0) ? ttbuinfo.commentsToRead : '';
});

const getProjBg = computed(() => {
  if (props.task && props.task.projectId) {
    const project = projectsList.value.find(p => p.id === props.task.projectId);
    if (project && project.backgroundShort) {
      return project.backgroundShort;
    }
  }
  return '';
});

const taskContentHidden = computed(() => !!props.task?.taskContentHidden);
const taskProjectName = computed(() => {
  const projectId = props.task?.projectId;
  return props.task?.projectName
    || props.task?.project?.name
    || projectsList.value?.find(project => project.id === projectId)?.name
    || '';
});
const hiddenTaskTooltip = computed(() => {
  const projectName = taskProjectName.value;
  if (!projectName) {
    return t('ui.hidden_task_tooltip_no_project');
  }
  return t('ui.hidden_task_tooltip_in_project', { projectName });
});


const previewBackdrop = computed(() => thumbHashToImageDataUrl(mainAttachment.value?.thumbHash));


const getPreview = () => {
  return props.task.attachments?.find(a => a.type === 'TASK_PREVIEW');
};

const mainAttachmentUrl = computed(() => {
  const preview = getPreview();
  if (!preview?.id) {
    return '';
  }
  return `${window.s3base}/attachments/${preview.workspaceId}/${props.task.projectId}`
    + `/${props.task.id}/${preview.id}.${preview.extension}`;
});

const mainAttachment = computed(() => {
  return props.task && props.task.attachments && getPreview();
});

const estimateCollector = computed(() => {
  let estimateSum = 0;
  if (props.task.estimate) {
    estimateSum += props.task.estimate;
  }
  if (props.task.isParentTask && props.task.childTasksDescriptor) {
    props.task.childTasksDescriptor.forEach(child => {
      if (child.estimate) {
        estimateSum += child.estimate;
      }
    });
  }
  return estimateSum;
});

const taskShortDescription = computed(() => {
  let result = props.task.textMD || '';

  if (result) {
    if (result > 88) {
      result = `${result.substring(0, 88)}...`;
    }
  } else result = '';

  return result;
});

// Subtask prefix for Kanban cards
const isSubtask = computed(() => !!props.task?.parentTaskId);
const computedParentPrefix = computed(() => {
  if (!isSubtask.value) return '';
  // Use backend-provided truncated prefix if available
  return props.task?.parentTaskDescriptor?.prefix || '';
});


// Methods


const updateAssignee = (userValue) => {
  emit('update', {
    id: props.task.id,
    stageId: props.task.stageId,
    projectId: props.task.projectId,
    assigneeId: userValue ? userValue.id : null,
    ...(
      (!props.task.executorId && userValue)
        ? { executorId: userValue.id } : {}
    ),
  });
};



const triggerFlashAnimation = () => {
  if (shouldShowUpd.value || notify.value) {
    shouldFlash.value = true;
    setTimeout(() => {
      shouldFlash.value = false;
    }, 800);
  }
};

const setupIntersectionObserver = () => {
  if (!taskRef.value) return;

  intersectionObserver.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Element is now visible
          if (!hasBeenInViewport.value) {
            // First time becoming visible
            hasBeenInViewport.value = true;
            triggerFlashAnimation();
          } else {
            // Re-entering viewport after being out of view
            triggerFlashAnimation();
          }
        }
      });
    },
    {
      threshold: 0.1, // Trigger when at least 10% of the element is visible
      rootMargin: '0px'
    }
  );

  intersectionObserver.value.observe(taskRef.value);
};

// Lifecycle
onMounted(() => {
  // Set up intersection observer for flash animation
  if ((shouldShowUpd.value || notify.value) && taskRef.value) {
    setupIntersectionObserver();
  }
});

onUnmounted(() => {
  // Clean up intersection observer
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
  }
});


</script>

<style lang="scss" scoped>

  :deep(.taskPreview.image-holder) {
    overflow: hidden;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    .img-wrapper[class] {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        height: 100%;
        width: auto;
        max-width: 100%;
      }
    }
  }

  .task {
    box-sizing: border-box;
    border-radius: var(--radius-primary);
    box-shadow: 0 1px 2px color-mix(in srgb, var(--shadow-color) 60%, transparent);
    max-width: var(--task-w, 300px);
    width: 100%;
    display: flex;
    flex-direction: column;

    .task-container {
      :deep(img) {
        max-width: 100%;
      }
      .task-data .tracker-data-priority-and-messages .labels-icon {
        display: none;
        width: 22px;
        &:has(.drop-wrapper.open) {
          display: flex;
          justify-content: center;
        }
      }
    }

    &.hoverable-task:hover {
      box-shadow: 1px 1px 8px var(--shadow-color);

      .task-container {
        .task-data .tracker-data-priority-and-messages .labels-icon {
          display: flex;
          justify-content: center;
        }
      }
    }
  }
</style>
